// Carhartt-WIP specific columns for the Purchase Order page.
import type { ColDef, ICellRendererParams } from "ag-grid-community"
import { Warning } from "@phosphor-icons/react"
import { createCustomColumn, createSumColumn } from "~/utils/columnTemplates"
import { dynamicParse } from "~/utils/tsUtils"
import { POColumnGroups } from "~/constants/purchaseOrderTable"
import type { ColumnChooserEntry } from "./poChooserCopy"
import type { RowDataType } from "./PurchaseOrderTableV2"
import type { DynamicColumnId } from "~/components/planning/ProductTable/utils/columnUtils"

// Re-exported for existing call sites; the hook itself lives in a dependency-light module so core
// providers can use it without pulling this column-heavy file (and its util graph) in.
export { useIsCarharttWip, CARHARTT_WIP_ORG } from "~/hooks/useIsCarharttWip"

const NEXT_SEASON_FIELD = "availableQuantityIncludingNextSeason"
const CARHARTT_SIMULATED_AVAILABILITY_COL_ID = "carharttSimulatedAvailability"
const CARHARTT_AVAILABILITY_VIOLATION_COL_ID = "carharttAvailabilityViolation"

type CarharttColumnResult = {
  colDef: ColDef<RowDataType>
  columnEntry: ColumnChooserEntry
}

function getSimulatedAvailability(data: RowDataType | undefined): number | undefined {
  if (!data) return undefined
  // True per-SKU value only — no fallback to the variant-level `extraData` (matches the Fashion
  // Supply Plan). Absent per-SKU value = 0.
  const nextSeason = dynamicParse(
    (data.extraDataSku?.[NEXT_SEASON_FIELD] as string | number | null | undefined) ?? 0,
  )
  const orderQty = data.quantity ?? 0
  return orderQty + (typeof nextSeason === "number" ? nextSeason : 0)
}

export function getCarharttSimulatedAvailabilityColumn(
  extraDataFields: string[],
): CarharttColumnResult | null {
  if (!extraDataFields.includes(`${NEXT_SEASON_FIELD}(extradata)`)) return null

  const colDef = createSumColumn<RowDataType>({
    colId: CARHARTT_SIMULATED_AVAILABILITY_COL_ID as DynamicColumnId,
    headerName: "Simulated availability",
    headerTooltip:
      "Order quantity + next-season available stock. Shows the resulting availability if you buy this quantity.",
    valueGetter: (params) => getSimulatedAvailability(params.data),
  })

  const columnEntry: ColumnChooserEntry = {
    id: CARHARTT_SIMULATED_AVAILABILITY_COL_ID,
    displayName: "Simulated availability",
    description:
      "Order quantity + next-season available stock (availableQuantityIncludingNextSeason).",
    group: POColumnGroups.ExternalData,
    keyWords: ["simulated availability", "next season", "available quantity"],
  }

  return { colDef, columnEntry }
}

export function getCarharttAvailabilityViolationColumn(
  extraDataFields: string[],
): CarharttColumnResult | null {
  if (!extraDataFields.includes(`${NEXT_SEASON_FIELD}(extradata)`)) return null

  const colDef = createCustomColumn<RowDataType>({
    colId: CARHARTT_AVAILABILITY_VIOLATION_COL_ID as DynamicColumnId,
    headerName: "Availability violation",
    headerTooltip:
      "Flagged when simulated availability is below zero. The order quantity does not cover unfulfilled orders, so increase the quantity to clear the violation.",
    minWidth: 120,
    wrapHeaderText: true,
    autoHeaderHeight: true,
    cellStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
    aggFunc: (params) => (params.values as unknown[]).some(Boolean),
    filter: "agSetColumnFilter",
    filterParams: {
      valueFormatter: (params: { value: unknown }) =>
        params.value === true ? "Violating" : params.value === false ? "OK" : "(blank)",
    },
    valueGetter: (params) => {
      const value = getSimulatedAvailability(params.data)
      return typeof value === "number" ? value < 0 : undefined
    },
    cellRenderer: (params: ICellRendererParams<RowDataType>) => {
      const isViolating = params.node?.group
        ? (params.node.allLeafChildren ?? []).some((leaf) => {
            const v = getSimulatedAvailability(leaf.data as RowDataType | undefined)
            return typeof v === "number" && v < 0
          })
        : !!params.value
      if (!isViolating) return null
      return (
        <Warning
          className="text-warning shrink-0"
          weight="fill"
          aria-label="Availability violation"
        />
      )
    },
  })

  const columnEntry: ColumnChooserEntry = {
    id: CARHARTT_AVAILABILITY_VIOLATION_COL_ID,
    displayName: "Availability violation",
    description:
      "Flagged when simulated availability is below zero. Parent rows are flagged if any SKU underneath violates.",
    group: POColumnGroups.ExternalData,
    keyWords: ["availability violation", "warning", "negative availability", "flag", "shortage"],
  }

  return { colDef, columnEntry }
}
